<template>
	<div class="chart-data-table">
		<div class="chart-data-table__content">
			<v-data-table
				:headers="headers"
				:items="tableData"
				:items-per-page="-1"
				hide-default-footer
				class="elevation-0"
				:height="maxHeight"
				fixed-header
			>
				<template #[`header.date`]="{ header }">
					<span class="header-text">
						{{ header.text }}
					</span>
				</template>
				<template
					v-for="seriesName in seriesNames"
					#[`header.${seriesName}`]="{ header }"
				>
					<span
						:key="`header-${seriesName}`"
						class="series-header"
						:title="header.text"
					>
						<span
							v-if="getColorForSeries(seriesName)"
							class="color-dot"
							:style="{ backgroundColor: getColorForSeries(seriesName) }"
						/>
						<span class="header-text">
							{{ truncateText(header.text, 18) }}
						</span>
					</span>
				</template>
				<template #[`header.total`]="{ header }">
					<span class="header-text">
						{{ header.text }}
					</span>
				</template>
				<template #[`item.date`]="{ item }">
					<div class="d-flex align-center ">
						<span class="text-body-2 font-weight-medium table-cell-text">
							{{ item.date }}
						</span>
					</div>
				</template>
				<template
					v-for="seriesName in seriesNames"
					#[`item.${seriesName}`]="{ item }"
				>
					<div
						:key="`cell-${seriesName}`"
						class="d-flex align-center justify-center"
					>
						<span class="text-body-2 table-cell-text numeric-cell">
							{{ formatNumber(item[seriesName]) }}
						</span>
					</div>
				</template>
				<template #[`item.total`]="{ item }">
					<div class="d-flex align-center justify-center">
						<span class="text-body-2 font-weight-medium table-cell-text numeric-cell">
							{{ formatNumber(item.total) }}
						</span>
					</div>
				</template>
				<template #no-data>
					<div class="text-center py-8">
						<v-icon
							size="48"
							color="grey lighten-1"
						>
							mdi-table-remove
						</v-icon>
						<div class="text-h6 grey--text mt-4">
							No data available
						</div>
					</div>
				</template>
			</v-data-table>
		</div>
	</div>
</template>

<script>
export default {
	name: 'chart-data-table',

	props: {
		data: {
			type: Array,
			required: true
		},
		seriesNames: {
			type: Array,
			required: true
		},
		colors: {
			type: Array,
			default: () => []
		},
		maxHeight: {
			type: [Number, String],
			default: 400
		}
	},

	computed: {
		headers () {
			const cols = [
				{ text: 'Date', value: 'date', sortable: true, align: 'left', width: '140px' }
			]

			this.seriesNames.forEach(name => {
				cols.push({
					text: name,
					value: name,
					sortable: true,
					align: 'center'
				})
			})

			cols.push({
				text: 'Total',
				value: 'total',
				sortable: true,
				align: 'center',
				width: '100px'
			})

			return cols
		},

		tableData () {
			return this.data.map(row => {
				let rowTotal = 0

				this.seriesNames.forEach(seriesName => {
					const value = row[seriesName] || 0
					const numericValue = typeof value === 'number' ? value : parseFloat(value) || 0
					rowTotal += numericValue
				})

				return {
					...row,
					total: rowTotal
				}
			})
		}
	},

	methods: {
		getColorForSeries (seriesName) {
			const index = this.seriesNames.indexOf(seriesName)
			return index >= 0 ? this.colors[index] : null
		},

		truncateText (text, maxLength = 18) {
			if (!text || text.length <= maxLength) return text
			return text.substring(0, maxLength) + '...'
		},

		formatNumber (value) {
			if (value === null || value === undefined) return '0'

			const numericValue = typeof value === 'number' ? value : parseFloat(value)

			if (isNaN(numericValue)) return '0'

			return numericValue.toLocaleString('en-GB', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 0
			})
		}
	}
}
</script>

<style scoped>
.chart-data-table {
	display: flex;
	flex-direction: column;
	background-color: var(--global-card-background, white);
	border-radius: var(--radius, 8px);
	overflow: hidden;
	min-height: 0;
	width: 100%;
}

.chart-data-table__content {
	flex: 1;
	overflow-x: auto;
	overflow-y: auto;
	min-height: 0;
	width: 100%;
}

.chart-data-table >>> .v-data-table {
	border: none;
	border-radius: 0;
}

.chart-data-table >>> .v-data-table table {
	min-width: 100%;
	table-layout: auto;
}

/* Header styling - matches file-loader */
.chart-data-table >>> .v-data-table thead th {
	font-weight: 500;
	font-size: 0.875rem;
	color: var(--global-muted-foreground, #6B7280);
	padding: 12px 16px !important;
	height: auto !important;
	vertical-align: middle !important;
	background-color: var(--global-card-background, white) !important;
	position: sticky;
	top: 0;
	z-index: 1;
	border-bottom: 1px solid var(--global-border-color, rgba(0, 0, 0, 0.12)) !important;
	white-space: nowrap;
}

/* Alternating row colors */
.chart-data-table >>> .v-data-table tbody tr:nth-child(odd) td {
	background-color: var(--global-card-background, white);
}

.chart-data-table >>> .v-data-table tbody tr:nth-child(even) td {
	background-color: var(--global-table-row-alt, rgba(0, 0, 0, 0.02));
}

/* Row hover */
.chart-data-table >>> .v-data-table tbody tr:hover td {
	background-color: var(--global-table-row-hover, rgba(0, 0, 0, 0.04)) !important;
}

/* Cell styling */
.chart-data-table >>> .v-data-table tbody td {
	padding: 12px 16px !important;
	height: auto !important;
	vertical-align: middle !important;
	border-bottom: 1px solid var(--global-border-color, rgba(0, 0, 0, 0.06)) !important;
	background-color: var(--global-card-background, white);
}

.chart-data-table >>> .v-data-table tbody tr {
	height: auto;
}

/* Sticky date column (first column) */
.chart-data-table >>> .v-data-table thead th:first-child {
	position: sticky;
	left: 0;
	z-index: 3;
	background-color: var(--global-card-background, white) !important;
}

.chart-data-table >>> .v-data-table tbody td:first-child {
	position: sticky;
	left: 0;
	z-index: 1;
}

.chart-data-table >>> .v-data-table tbody tr:nth-child(odd) td:first-child {
	background-color: var(--global-card-background, white) !important;
}

.chart-data-table >>> .v-data-table tbody tr:nth-child(even) td:first-child {
	background-color: var(--global-table-row-alt, rgba(0, 0, 0, 0.02)) !important;
}

/* Header text */
.header-text {
	font-weight: 500;
	font-size: 0.875rem;
	color: var(--global-muted-foreground, #6B7280);
}

/* Series header with color dot */
.series-header {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	white-space: nowrap;
}

.color-dot {
	display: inline-block;
	width: 8px;
	height: 8px;
	border-radius: 50%;
	flex-shrink: 0;
}

/* Table cell text color */
.table-cell-text {
	color: var(--global-foreground, #374151) !important;
}

/* Numeric cells */
.numeric-cell {
	font-variant-numeric: tabular-nums;
}

/* Dark mode */
.theme--dark .chart-data-table {
	background-color: var(--global-card-background, #1c1c1e);
}

.theme--dark .chart-data-table >>> .v-data-table thead th {
	background-color: var(--global-card-background, #1c1c1e) !important;
	border-color: var(--global-border, #374151) !important;
	color: var(--global-muted-foreground, #9ca3af);
}

.theme--dark .chart-data-table >>> .v-data-table tbody td {
	border-color: var(--global-border, #374151) !important;
	background-color: var(--global-card-background, #1c1c1e);
}

.theme--dark .chart-data-table >>> .v-data-table thead th:first-child {
	background-color: var(--global-card-background, #1c1c1e) !important;
}

.theme--dark .chart-data-table >>> .v-data-table tbody tr:nth-child(odd) td:first-child {
	background-color: var(--global-card-background, #1c1c1e) !important;
}

.theme--dark .chart-data-table >>> .v-data-table tbody tr:nth-child(even) td:first-child {
	background-color: var(--global-table-row-alt, rgba(255, 255, 255, 0.02)) !important;
}

.theme--dark .chart-data-table >>> .v-data-table tbody tr:nth-child(odd) td {
	background-color: var(--global-card-background, #1c1c1e);
}

.theme--dark .chart-data-table >>> .v-data-table tbody tr:nth-child(even) td {
	background-color: var(--global-table-row-alt, rgba(255, 255, 255, 0.02));
}

.theme--dark .chart-data-table >>> .v-data-table tbody tr:hover td {
	background-color: var(--global-table-row-hover, rgba(255, 255, 255, 0.06)) !important;
}

.theme--dark .table-cell-text {
	color: var(--global-foreground, #f9fafb) !important;
}

.theme--dark .header-text {
	color: var(--global-muted-foreground, #9ca3af);
}

/* Dark mode scrollbar styling */
.theme--dark .chart-data-table__content::-webkit-scrollbar {
	width: 8px;
	height: 8px;
	background: var(--global-card-background, #1c1c1e);
}

.theme--dark .chart-data-table__content::-webkit-scrollbar-track {
	background: var(--global-card-background, #1c1c1e);
}

.theme--dark .chart-data-table__content::-webkit-scrollbar-thumb {
	background: var(--global-border, #374151);
	border-radius: 4px;
}

.theme--dark .chart-data-table__content::-webkit-scrollbar-thumb:hover {
	background: var(--global-muted-foreground, #6b7280);
}

.theme--dark .chart-data-table__content::-webkit-scrollbar-corner {
	background: var(--global-card-background, #1c1c1e);
}

/* Also style the v-data-table wrapper for dark mode */
.theme--dark .chart-data-table >>> .v-data-table__wrapper {
	background-color: var(--global-card-background, #1c1c1e);
}

.theme--dark .chart-data-table >>> .v-data-table__wrapper::-webkit-scrollbar {
	width: 8px;
	height: 8px;
	background: var(--global-card-background, #1c1c1e);
}

.theme--dark .chart-data-table >>> .v-data-table__wrapper::-webkit-scrollbar-track {
	background: var(--global-card-background, #1c1c1e);
}

.theme--dark .chart-data-table >>> .v-data-table__wrapper::-webkit-scrollbar-thumb {
	background: var(--global-border, #374151);
	border-radius: 4px;
}

.theme--dark .chart-data-table >>> .v-data-table__wrapper::-webkit-scrollbar-corner {
	background: var(--global-card-background, #1c1c1e);
}
</style>
